import React, { useEffect, useState, useCallback } from "react";
import { actionCenterApi } from "../../services/actionCenterApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useLocalization } from "../../hooks/useLocalization";

const SEVERITY_COLORS = { LOW: "text-gray-500", MEDIUM: "text-amber-600", HIGH: "text-orange-600", CRITICAL: "text-red-600" };

export default function ActionCenterPage() {
  const { t } = useLocalization();
  const { canViewManagerDigest } = usePermissions();
  const [digest, setDigest] = useState(null);
  const [filters, setFilters] = useState({ module: "", severity: "" });

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    actionCenterApi.getDigest(params).then((r) => setDigest(r.data)).catch(() => {});
  }, [filters]);

  useEffect(() => { if (canViewManagerDigest) load(); }, [canViewManagerDigest, load]);

  if (!canViewManagerDigest) return <p className="text-sm text-gray-500" data-testid="action-center-access-denied">{t("common.accessDenied")}</p>;

  return (
    <div className="space-y-5" data-testid="action-center-page">
      <PageHelpBanner pageKey="action-center" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtActionCenter.help")}</p>
      </PageHelpBanner>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtActionCenter.title")}</h2>
          <p className="text-sm text-gray-500">{digest?.total_items ?? "-"} {t("mgmtActionCenter.subtitle")}</p>
        </div>
        <a data-testid="download-digest-pdf-link" href={actionCenterApi.getDigestPdfUrl()} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50">{t("mgmtActionCenter.downloadPdf")}</a>
      </div>

      {digest && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(digest.counts_by_severity).map(([sev, count]) => (
            <button key={sev} data-testid={`severity-filter-${sev}`} onClick={() => setFilters({ ...filters, severity: filters.severity === sev ? "" : sev })}
              className={`bg-white border rounded-xl p-4 text-left ${filters.severity === sev ? "border-blue-400" : "border-gray-200"}`}>
              <p className={`text-2xl font-bold ${SEVERITY_COLORS[sev]}`}>{count}</p>
              <p className="text-xs text-gray-500">{sev}</p>
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {digest && Object.keys(digest.counts_by_module).map((m) => (
          <button key={m} data-testid={`module-filter-${m}`} onClick={() => setFilters({ ...filters, module: filters.module === m ? "" : m })}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${filters.module === m ? "bg-blue-600 text-white border-blue-600" : "border-gray-200 bg-white hover:bg-gray-50"}`}>
            {m} ({digest.counts_by_module[m]})
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtActionCenter.colModule")}</th><th className="px-4 py-3 font-medium">{t("mgmtActionCenter.colCategory")}</th><th className="px-4 py-3 font-medium">{t("mgmtActionCenter.colSeverity")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colEmployee")}</th><th className="px-4 py-3 font-medium">{t("mgmtActionCenter.colDue")}</th><th className="px-4 py-3 font-medium">{t("mgmtActionCenter.colMessage")}</th></tr></thead>
          <tbody>
            {(digest?.items || []).map((i, idx) => (
              <tr key={idx} className="border-t border-gray-100" data-testid={`digest-item-row-${idx}`}>
                <td className="px-4 py-2 text-gray-600">{i.module}</td>
                <td className="px-4 py-2 text-gray-600">{i.category}</td>
                <td className="px-4 py-2"><StatusBadge status={i.severity} /></td>
                <td className="px-4 py-2 text-gray-600">{i.employee_name || "-"}</td>
                <td className="px-4 py-2 text-gray-500">{i.due_date || "-"}</td>
                <td className="px-4 py-2 text-gray-500">{i.message}</td>
              </tr>
            ))}
            {digest && digest.items.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500" data-testid="no-digest-items-row">{t("mgmtActionCenter.noItems")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
